import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { User } from "../../types/User";
import { getAllUsers } from "../../services/userService";
import { useAuth } from "./authContext";

interface AllUsersContextProps {
  users: User[];
  setUsers: React.Dispatch<React.SetStateAction<User[]>>;
  isLoading: boolean;
  error: string | null;
}

const AllUsersContext = createContext<AllUsersContextProps | undefined>(
  undefined
);

export const AllUsersProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // /api/User/all requires a session, so skip it until the user is signed in
    // (otherwise the login page fires an unauthorized request). Re-runs on
    // login/logout to load or clear the cached list.
    if (!isAuthenticated) {
      setUsers([]);
      setError(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    getAllUsers()
      .then((data) => {
        setUsers(data);
        setError(null);
      })
      .catch(() => setError("Failed to load users"))
      .finally(() => setIsLoading(false));
  }, [isAuthenticated]);

  return (
    <AllUsersContext.Provider value={{ users, setUsers, isLoading, error }}>
      {children}
    </AllUsersContext.Provider>
  );
};

export const useAllUsers = (): AllUsersContextProps => {
  const context = useContext(AllUsersContext);
  if (!context) {
    throw new Error("useAllUsers must be used within an AllUsersProvider");
  }
  return context;
};
